import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { deepSetObject, digObject } from '@domain-group/javascript-utils'

import usePublicationEditions from '@hooks/usePublicationEditions'

import usePublicationSpecialDeadline, { usePublicationSpecialDeadlineForm } from '@hooks/usePublicationSpecialDeadline'

import formatDate, { formatToDateTime } from '@functions/formatDate'

const handleDeadlineChange = (value, key, options, setEntityState) => {
  const dateTime = formatDate(value, "yyyy-MM-dd'T'HH:mm", 'dd/MM/yyyy HH:mm')
  const updatedOptions = deepSetObject(options, `deadlines.${key}`, dateTime)

  setEntityState({ options: updatedOptions })
}

const CreatePublicationSpecialDeadlineModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, createPublicationSpecialDeadline } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { publicationSpecialDeadline: initSpecialDeadline } = modalPayload

  const {
    callbacks: {
      createPublicationSpecialDeadline: createFn,
    },
    creating,
    publicationSpecialDeadline,
    publicationSpecialDeadline: { publication_id },
  } = usePublicationSpecialDeadline(initSpecialDeadline)

  const {
    filteredPublicationEditions,
  } = usePublicationEditions({
    filters: { min_publication_date: DateTime.now().minus({ months: 1 }).toFormat('yyyy-MM-dd'), publication_id },
    performHttpRequests: !!publication_id,
  })

  const {
    entityState,
    entityState: {
      options,
      publication_edition_id,
      title,
    },
    errors,
    handlers,
    handlers: { onBlur },
    saveEnabled,
    setEntityState,
  } = usePublicationSpecialDeadlineForm(publicationSpecialDeadline)

  const artwork = digObject(options, 'deadlines.artwork', '')
  const booking = digObject(options, 'deadlines.booking', '')
  const ofi = digObject(options, 'deadlines.ofi', '')

  const createPublicationSpecialDeadlinePayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Special Deadline Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Special Deadline" />

      <SidebarModal.Content>
        <Form>
          <Form.Field direction="column" errorMessage={errors.title} label="* Title">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field
            direction="column"
            errorMessage={errors.publication_edition_id}
            label="* Publication Edition"
            marginTop="large"
          >
            <select name="publication_edition_id" value={publication_edition_id} {...handlers}>
              <option>Please Select...</option>
              {filteredPublicationEditions.map(edition => (
                <option key={edition.id} value={edition.id}>{edition.title}</option>
              ))}
            </select>
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'medium' }}>Booking Deadline (AEST)</SectionDivider>

          <Form.Field direction="column" errorMessage={errors['options.deadlines.booking']} label="* Date">
            <input
              name="options.deadlines.booking"
              onBlur={onBlur}
              onChange={e => handleDeadlineChange(e.target.value, 'booking', options, setEntityState)}
              type="datetime-local"
              value={formatToDateTime(booking)}
            />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'medium' }}>Artwork Deadline (AEST)</SectionDivider>

          <Form.Field direction="column" errorMessage={errors['options.deadlines.artwork']} label="* Date">
            <input
              name="options.deadlines.artwork"
              onBlur={onBlur}
              onChange={e => handleDeadlineChange(e.target.value, 'artwork', options, setEntityState)}
              type="datetime-local"
              value={formatToDateTime(artwork)}
            />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'medium' }}>OFI Deadline (AEST)</SectionDivider>

          <Form.Field direction="column" errorMessage={errors['options.deadlines.ofi']} label="* Date">
            <input
              name="options.deadlines.ofi"
              onBlur={onBlur}
              onChange={e => handleDeadlineChange(e.target.value, 'ofi', options, setEntityState)}
              type="datetime-local"
              value={formatToDateTime(ofi)}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createPublicationSpecialDeadline(createPublicationSpecialDeadlinePayload)}
          size="large"
        >
          {saveEnabled ? 'Create Special Deadline' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreatePublicationSpecialDeadlineModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreatePublicationSpecialDeadlineModal.defaultProps = {
  modalKey: 'CreatePublicationSpecialDeadlineModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreatePublicationSpecialDeadlineModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
